import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { combine, createStore, Store } from 'effector';
import { RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { createPaginatedSchema, PlayerRegistryItem, playerRegistryItemSchema } from '../types';

export type OriginalPlayerRegistryParams = {
	sortField?: string | null;
	sortDirection?: string;
	page?: number;
	limit?: number;
	name?: string;
	accountIds?: string[];
	isConnected?: string[];
	currencies?: string[];
	deviceIds?: string[];
	gameIds?: string[];
	cashdeskIds?: string[];
};

export type PlayerRegistryFilters = {
	name: string;
	accountIds: number[];
	isConnected: boolean[];
	currencies: string[];
	deviceIds: number[];
	gameIds: number[];
	cashdeskIds: number[];
};

export type PlayerRegistryParams = RegistryParams<PlayerRegistryFilters>;

export type PlayerRegistryQuery = RegistryQuery<
	PlayerRegistryItem,
	PlayerRegistryFilters,
	JsonApiRequestError
>;

export const createPlayerRegistryQuery = (
	$accountId?: Store<number | null>,
	$cashdeskId?: Store<number | null>,
): PlayerRegistryQuery =>
	createAuthQuery({
		params: declareParams<PlayerRegistryParams>(),
		request: {
			method: 'GET',
			url: '/player/list',
			query: {
				source: combine([
					$accountId ?? createStore<number | null>(null),
					$cashdeskId ?? createStore<number | null>(null),
				]),
				fn: (params, [accountId, cashdeskId]) => {
					const cashdeskIds = [
						...(cashdeskId ? [String(cashdeskId)] : []),
						...params.filters.cashdeskIds.map(String),
					];

					return {
						page: params.page,
						limit: params.limit,
						sortField: params.sortField ?? undefined,
						sortDirection: params.sortDirection,
						name: params.filters.name || undefined,
						isConnected:
							params.filters.isConnected.length === 0
								? undefined
								: params.filters.isConnected.map(String),
						currencies:
							params.filters.currencies.length === 0 ? undefined : params.filters.currencies,
						deviceIds:
							params.filters.deviceIds.length === 0
								? undefined
								: params.filters.deviceIds.map(String),
						gameIds:
							params.filters.gameIds.length === 0 ? undefined : params.filters.gameIds.map(String),
						cashdeskIds: cashdeskIds.length === 0 ? undefined : cashdeskIds,
						accountIds: [
							...(accountId ? [String(accountId)] : []),
							...params.filters.accountIds.map(String),
						],
					};
				},
			},
		},
		response: {
			contract: zodContract(createPaginatedSchema(playerRegistryItemSchema)),
		},
	});
